import React, { useEffect } from 'react';
import PrivateRoute from '@shared/routing/PrivateRoute';
import { Switch, useHistory, useLocation } from 'react-router';
import '@features/settings/pages/user-management.scss';
import { app } from "@microsoft/teams-js";
import { isInTeams } from "@shared/utils/TeamsUtils";
import { WithOfficeUsagesLoader } from "@features/desks/hooks/WithOfficeUsagesLoader";
import {
  TeamSearchWhereaboutsPage,
  TeamWhereaboutsPage,
  WorkgroupWhereaboutsPage
} from "../whereabouts-v2/ScopedWhereaboutsPages";
import { RoomFinderPage } from "@features/meeting-rooms/pages/pages/RoomFinderPage";
import { RoomBookings } from "@features/meeting-rooms/pages/pages/RoomBookings";
import { AddUsersPage } from "@features/settings/pages/manage-users-page-v2/AddUsersPage";
import { WorkPlannerPage } from "@features/wall-planner/pages/WorkPlannerPage";
import { OfficeFloorMapView } from "../company-movements/components/OfficeFloorMapView";
import { DailyOfficeListView } from "../company-movements/components/daily-office-list-view/DailyOfficeListView";
import { CompanyTeamView } from "../company-movements/components/CompanyTeamView";
import { CompanyTagsView } from "../company-movements/components/CompanyTagsView";
import { VisitorBookingView } from "../company-movements/components/visitor-booking/VisitorBookingView";
import { LockerDirectoryPage } from "../lockers/LockerDirectoryPage";
import { LockerWaitingListPage } from "../lockers/LockerWaitingListPage";
import { DepartmentsListPage } from "../departments/DepartmentsListPage";
import { DepartmentsUserPage } from "../departments/DepartmentsUserPage";
import { WallPlannerNewEventPage } from "@features/wall-planner/pages/WallPlannerNewEventPage";
import { UsersWhereaboutsReport } from "@features/settings/pages/user-whereabouts-report/UsersWhereaboutsReport";
import PersonalPreferencesPage from "@features/settings/pages/PersonalPreferencesPage";
import Layout from "@shared/ui/organisms/layout/Layout";
import { HospitalityOrdersPage } from "@features/meeting-rooms/pages/pages/HospitalityOrdersPage";
import WhereaboutsPageV2 from "../whereabouts-v2/WhereaboutsPageV2";

function PersonalPreferencesPageInWhereabouts() {
  return (
    <div className="managePage__container">
      <Layout>
        <div className="managePage">
          <PersonalPreferencesPage />
        </div>
      </Layout>
    </div>
  );
}

export default function WhereaboutsRouting() {
  const location = useLocation();
  const history = useHistory();

  WithOfficeUsagesLoader();

  useEffect(() => {
    if (!isInTeams()) return;

    app.getContext().then((context: app.Context) => {
      if (!context.page.subPageId) return;
      const parts = context.page.subPageId.split(':');
      if (parts.length < 2) return;

      const targetBySubPage: Record<string, string> = {
        search: `/whereabouts/team/search/${parts[1]}`,
        team: `/whereabouts/team/${parts[1]}`,
        tag: `/whereabouts/tag/${parts[1]}`,
      };
      const target = targetBySubPage[parts[0]];
      if (target && location.pathname !== target) {
        history.push(target);
      }
    })
  }, [location.pathname, history]);

  return (
    <Switch>
      <PrivateRoute exact path="/whereabouts/team/add-users" component={AddUsersPage} />
      <PrivateRoute exact path="/whereabouts/team" component={WhereaboutsPageV2}/>
      <PrivateRoute exact path="/whereabouts/my-summary" component={UsersWhereaboutsReport}/>
      <PrivateRoute exact path="/whereabouts/preferences" component={PersonalPreferencesPageInWhereabouts}/>
      <PrivateRoute exact path="/whereabouts/team/:teamId" component={TeamWhereaboutsPage}/>
      <PrivateRoute exact path="/whereabouts/team/search/:teamId" component={TeamSearchWhereaboutsPage}/>
      <PrivateRoute exact path="/whereabouts/tag/:tagId" component={WorkgroupWhereaboutsPage}/>

      <PrivateRoute exact path="/whereabouts/work-planner" component={WorkPlannerPage}/>
      <PrivateRoute exact path="/whereabouts/work-planner/event" component={WallPlannerNewEventPage}/>
      <PrivateRoute exact path="/whereabouts/work-planner/event/:eventId" component={WallPlannerNewEventPage}/>
      <PrivateRoute exact path="/whereabouts/floor-planner" component={OfficeFloorMapView}/>
      <PrivateRoute exact path="/whereabouts/office-view" component={DailyOfficeListView}/>
      <PrivateRoute exact path="/whereabouts/visitor-booking/:officeId" component={VisitorBookingView}/>

      <PrivateRoute exact path="/whereabouts/company" component={CompanyTeamView}/>
      <PrivateRoute exact path="/whereabouts/company/all" component={CompanyTeamView}/>
      <PrivateRoute exact path="/whereabouts/company/workgroups" component={CompanyTagsView}/>
      <PrivateRoute exact path="/whereabouts/company/add-users" component={AddUsersPage} />
      <PrivateRoute exact path="/whereabouts/company/team/:teamId" component={TeamWhereaboutsPage}/>
      <PrivateRoute exact path="/whereabouts/company/tag/:tagId" component={WorkgroupWhereaboutsPage}/>
      <PrivateRoute exact path="/whereabouts/company/lockers" component={LockerDirectoryPage}/>
      <PrivateRoute exact path="/whereabouts/company/lockers/waiting-list" component={LockerWaitingListPage}/>
      <PrivateRoute exact path="/whereabouts/company/departments" component={DepartmentsListPage}/>
      <PrivateRoute exact path="/whereabouts/company/departments/:departmentId" component={DepartmentsUserPage}/>

      <PrivateRoute exact path="/whereabouts/meeting-rooms" component={RoomFinderPage}/>
      <PrivateRoute exact path="/whereabouts/meeting-rooms/my-bookings" component={RoomBookings}/>
      <PrivateRoute exact path="/whereabouts/meeting-rooms/hospitality-orders" component={HospitalityOrdersPage}/>
    </Switch>
  )
}
